import { useCallback, useEffect, useState } from "react";

import { normalizeDeviceLabel } from "./mediaStreamUtils";
import type { MediaInputDevice } from "./mediaTypes";

interface UseMediaDevicesReturn {
  cameraDevices: MediaInputDevice[];
  micDevices: MediaInputDevice[];
  refreshDevices: () => Promise<void>;
}

export const useMediaDevices = (): UseMediaDevicesReturn => {
  const [devices, setDevices] = useState<{
    cameras: MediaInputDevice[];
    mics: MediaInputDevice[];
  }>({ cameras: [], mics: [] });

  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const allDevices = await navigator.mediaDevices.enumerateDevices();
    setDevices({
      cameras: allDevices
        .filter(device => device.kind === "videoinput" && device.label)
        .map(device => ({
          deviceId: device.deviceId,
          label: normalizeDeviceLabel(device.label),
        })),
      mics: allDevices
        .filter(device => device.kind === "audioinput" && device.label)
        .map(device => ({
          deviceId: device.deviceId,
          label: normalizeDeviceLabel(device.label),
        })),
    });
  }, []);

  useEffect(() => {
    void refreshDevices();
    if (!navigator.mediaDevices?.addEventListener) return;
    const handleDeviceChange = () => {
      void refreshDevices();
    };
    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        handleDeviceChange,
      );
    };
  }, [refreshDevices]);

  return {
    cameraDevices: devices.cameras,
    micDevices: devices.mics,
    refreshDevices,
  };
};
